<script>
import VueEasyLightbox from "vue-easy-lightbox";
import {getImagePath} from "@/helpers/utils";
import PageProfile from "@/components/pages/page-profile";
import PageForm from "@/components/pages/page-form";
import InventoryDetails from "../inventories/inventory-details";
import PersonDetails from "@/components/persons/person-details";
import ScrollLoad from "@/components/scroll-load.vue";

export default {
  components: {
    VueEasyLightbox,
    PageProfile,
    PageForm,
    InventoryDetails,
    PersonDetails,
    ScrollLoad
  },
  props: {
    pages: {
      type: Array,
      required: true,
    },
    canEdit: {
      type: Boolean,
      required: true,
    },
    fields: {
      type: Array,
      default: () => [
        {key: "NumPageAlbum", sortable: true, label: '#'},
        {key: "image", sortable: false, label: 'Image'},
        {key: "date", sortable: true},
        {key: "address", sortable: true, label: 'Adresse'},
        {key: "persons", sortable: false, label: 'Personnes'},
        {key: "inscription_commentaire", sortable: false, label: 'Inscription / Commentaire', class: "w-50"},
        {key: "actions", sortable: false, class: 'text-center'},
      ]
    },
    perPage: {
      type: Number,
      default: 25
    },
    autoUpdateTotalRows: {
      type: Boolean,
      default: true
    },
    totalRows: {
      type: Number,
      default: 0
    },
    currentPage: {
      type: Number,
      default: 1
    },
    showEdit: {
      type: Boolean,
      default: true
    },
    loading: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      singleImage: false,
      lightboxImgs: [],
      sortBy: "NumPageAlbum",
      sortDesc: false,
      modalTitle: '',
      showModal: false,
      displayDetails: false,
      displayEditForm: false,
      displayedInventory: null,
      selectedPerson: null,
      displayInventoryDetails: false,
      localCurrentPage: this.autoUpdateTotalRows ? 1 : this.currentPage,
      localTotalRows: /*this.autoUpdateTotalRows ? this.pages.length :*/ this.totalRows,
    };
  },
  watch: {
    totalRows(newVal) {
      this.localTotalRows = newVal;
    },
    pages(newVal) {
      this.localTotalRows = this.autoUpdateTotalRows ? newVal.length : this.localTotalRows;
    },
    currentPage(v) {
      this.localCurrentPage = v;
    },
    localCurrentPage() {
      this.$emit('currentpagechanged', this.localCurrentPage);
    }
  },
  methods: {
    formatDate(item) {
      let date = '';

      if (item.DateJour !== '' && item.DateJour !== ' ')
        date += item.DateJour;
      if (item.DateMois !== '' && item.DateMois !== ' ')
        date += (date !== '' ? '/' : '') + item.DateMois;
      if (item.DateAnnee !== '' && item.DateAnnee !== ' ')
        date += (date !== '' ? '/' : '') + item.DateAnnee;

      return date;
    },
    formatAdresse(item) {
      let adresse = '';

      if (item.Adresse !== '' && item.Adresse !== ' ')
        adresse += item.Adresse;
      if (item.Ville !== '' && item.Ville !== ' ')
        adresse += (adresse !== '' ? ' - ' : '') + item.Ville;
      if (item.Region !== '' && item.Region !== ' ')
        adresse += (adresse !== '' ? ' - ' : '') + item.Region;
      if (item.Pays !== '' && item.Pays !== ' ')
        adresse += (adresse !== '' ? ' - ' : '') + item.Pays;

      return adresse;
    },
    getImagePath(item, thumbnails) {
      return getImagePath(item, thumbnails);
    },
    clickedImage(item) {
      this.lightboxImgs = getImagePath(item);
      this.singleImage = true;
    },
    showPageDetails(page) {
      this.displayDetails = true;
      this.displayEditForm = false;
      this.selectedPage = page;
      this.modalTitle = page.NumPageAlbum;
    },
    showPageEditForm(page) {
      this.displayDetails = false;
      this.displayEditForm = true;
      this.selectedPage = page;
      this.modalTitle = '[EDIT]' + page.NumPageAlbum;
    },
    hideModal() {
      this.displayDetails = false;
      this.displayEditForm = false;
      this.selectedPage = null;
      this.modalTitle = '';

      this.$bvModal.hide('modal-page-details');
    },
    isBis(page) {
      if (!page.NumPageAlbum) {
        return false;
      }

      return page.NumPageAlbum.match(/bis|ter/i) !== null;
    }
  }
};
</script>

<template>
  <div class="table mb-0">
    <b-table ref="pages_table" :items="pages" :fields="fields" responsive="sm" striped sticky-header
             :sort-by="sortBy" :sort-desc="sortDesc" :no-local-sorting="!autoUpdateTotalRows" head-variant="light">
      <template #cell(NumPageAlbum)="data">
        <b>{{ data.item.NumPageAlbum }}</b>

        <span v-if="isBis(data.item)" role="button" class="badge bg-info font-size-11 m-1 float-end">
            BIS
        </span>

        <div v-for="(inventory, i) in data.item.inventories" :key="i">
          <span role="button" @click="displayInventoryDetails = true; displayedInventory = inventory"
                class="badge badge-soft-info">{{ inventory.inventory_number }}</span>
        </div>
      </template>

      <template #cell(image)="data">
        <a v-if="data.item.image_available" href="javascript: void(0)" @click="clickedImage(data.item)">
          <b-img-lazy :key="data.item.CodePageAlbum" :src="getImagePath(data.item, true)[0].src"
                      :alt="data.item.NumPageAlbum" class="img-album"/>
        </a>
      </template>

      <template #cell(date)="data">
        <div>{{ formatDate(data.item) }}</div>
        <div v-if="data.item.DetailDate !== ' '"><small>{{ data.item.DetailDate }}</small></div>
      </template>

      <template #cell(address)="data">
        {{ formatAdresse(data.item) }}
      </template>

      <template #cell(inscription_commentaire)="data">
        <div v-if="data.item.Inscription !== '' && data.item.Inscription !== ' '"><strong>Inscription : </strong>
          {{ data.item.Inscription }}
        </div>
        <div v-if="data.item.Commentaire !== '' && data.item.Commentaire !== ' '"><strong>Commentaire : </strong>
          {{ data.item.Commentaire }}
        </div>
      </template>

      <template #cell(actions)="data">
        <ul class="list-inline font-size-20 contact-links mb-0">
          <li class="list-inline-item text-info">
            <a v-b-modal.modal-page-details @click="showPageDetails(data.item)"
               title="Afficher">
              <i class="bx bx-show"></i>
            </a>
          </li>
          <li v-if="canEdit" class="list-inline-item text-warning">
            <a v-b-modal.modal-page-details @click="showPageEditForm(data.item)"
               title="Modify">
              <i class="bx bx-pencil"></i>
            </a>
          </li>
        </ul>
      </template>

      <template #cell(persons)="data">
        <div v-if="data.item.persons">
          <div class="me-1 d-inline-block" v-for="(person, i) in data.item.persons" :key="i">
            <a class="text-primary text-capitalize" role="button" v-b-modal.modal-person
               @click="selectedPerson = person"
               :title="person.last_name + ' ' + person.first_name">
              <span class="badge text-primary border-primary bg-secondary">
                {{ person.last_name + ' ' + person.first_name }}
              </span>
            </a>
          </div>
        </div>
      </template>

      <template #custom-foot>
        <tr>
          <th colspan="100%">
            <scroll-load v-show="!autoUpdateTotalRows" v-model="localCurrentPage" :total-rows="localTotalRows"
                         :per-page="perPage" :loading="loading"></scroll-load>
          </th>
        </tr>
      </template>
    </b-table>

    <!--<div class="dataTables_paginate paging_simple_numbers float-right">
      <ul class="pagination pagination-rounded mb-0">
        <b-pagination v-model="localCurrentPage" :total-rows="localTotalRows" :per-page="perPage"></b-pagination>
      </ul>
    </div>
    <div class="float-end">
      <strong>{{ localTotalRows }}</strong> enregistrements
    </div>-->

    <vue-easy-lightbox :visible="singleImage" :imgs="lightboxImgs" @hide="singleImage = false"></vue-easy-lightbox>

    <b-modal id="modal-page-details" :title="this.modalTitle" centered hide-footer size="xl" title-class="font-18"
             @hide="hideModal">
      <page-profile v-if="displayDetails" :page="this.selectedPage"></page-profile>

      <template v-if="canEdit">
        <page-form v-if="displayEditForm" :page="this.selectedPage" v-on:cancel="hideModal"
                   v-on:submitted="hideModal"></page-form>
      </template>
    </b-modal>

    <b-modal centered hide-footer size="xl" title-class="font-18" v-model="displayInventoryDetails"
             @hide="displayedInventory = null">
      <inventory-details v-if="displayedInventory" :resource="displayedInventory"></inventory-details>
    </b-modal>

    <b-modal id="modal-person" centered hide-footer size="md" title-class="font-18" @hide="selectedPerson = null">
      <person-details v-if="selectedPerson" :person="this.selectedPerson"></person-details>
    </b-modal>
  </div>
</template>
